import React, { useEffect, useState } from "react";
import { Search, ChevronLeft, ChevronRight, UserX, Ban, ShieldAlert, KeyRound, Crown, Unplug } from "lucide-react";
import { authApi } from "../../lib/auth";
import { Input } from "../ui/input";

const FILTERS = [
  ["all", "All"],
  ["licensed", "Licensed"],
  ["unlicensed", "No license"],
  ["disabled", "Disabled + licensed"],
  ["inactive", "Inactive 90d"],
  ["overlap", "Multiple suites"],
  ["gaps", "License gaps"],
  ["nomfa", "No MFA"],
  ["admins", "Admins"],
  ["guests", "Guests"],
];
const GAP_LABEL = { entra_p1: "needs Entra P1", entra_p2: "needs Entra P2", intune_p1: "needs Intune P1" };

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—");

export default function ScanUsersTable({ scanId, summary, initialFilter = "all" }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const [sku, setSku] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const size = 50;

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      authApi.scans
        .users(scanId, { q, filter, sku: sku || undefined, page, size })
        .then(setData)
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [scanId, q, filter, sku, page]);

  const pages = Math.max(1, Math.ceil(data.total / size));
  return (
    <section className="ws-card glass" data-testid="scan-users">
      <div className="ws-card-head wrap">
        <h2>
          Users <span className="muted">{data.total.toLocaleString()} shown</span>
        </h2>
        <div className="ws-filters">
          <div className="ws-search">
            <Search size={14} />
            <Input value={q} onChange={(e) => (setQ(e.target.value), setPage(1))} placeholder="Search name, UPN, department" data-testid="users-search" />
          </div>
          <select value={sku} onChange={(e) => (setSku(e.target.value), setPage(1))} className="ws-native-select" data-testid="users-sku-filter">
            <option value="">Any license</option>
            {summary.skus
              .filter((s) => s.consumed > 0)
              .map((s) => (
                <option key={s.sku_id} value={s.sku_id}>
                  {`${s.name} (${s.consumed})`}
                </option>
              ))}
          </select>
        </div>
      </div>
      <div className="pills" role="group" aria-label="User filters">
        {FILTERS.map(([id, label]) => (
          <button key={id} type="button" className="pill" aria-pressed={filter === id} onClick={() => (setFilter(id), setPage(1))} data-testid={`users-filter-${id}`}>
            {label}
          </button>
        ))}
      </div>
      <div className={`ws-table-wrap ${loading ? "loading" : ""}`}>
        <table className="ws-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Department</th>
              <th>Licenses</th>
              <th>Last sign-in</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((u) => (
              <tr key={u.graph_id} data-testid="user-row">
                <td>
                  <div className="sku-name">{u.display_name}</div>
                  <div className="sku-part mono">{u.upn}</div>
                </td>
                <td>{u.department || "—"}</td>
                <td>
                  <div className="lic-chips">
                    {u.licenses.length === 0 && <span className="muted">none</span>}
                    {u.licenses.map((l) => (
                      <span key={l} className="mini-chip">
                        {l}
                      </span>
                    ))}
                  </div>
                </td>
                <td className={u.inactive && u.licenses.length ? "warn" : ""}>{summary.users.sign_in_available ? fmtDate(u.last_sign_in) : "n/a"}</td>
                <td>
                  {u.guest && <span className="mini-chip">guest</span>}
                  {!u.enabled && (
                    <span className="mini-chip warn">
                      <Ban size={10} /> disabled
                    </span>
                  )}
                  {u.inactive && u.enabled && u.licenses.length > 0 && (
                    <span className="mini-chip warn">
                      <UserX size={10} /> inactive
                    </span>
                  )}
                  {u.mfa_registered === false && u.enabled && !u.guest && (
                    <span className="mini-chip warn" data-testid="user-nomfa">
                      <KeyRound size={10} /> no MFA
                    </span>
                  )}
                  {(u.admin_roles || []).length > 0 && (
                    <span className="mini-chip" title={u.admin_roles.join(", ")} data-testid="user-admin">
                      <Crown size={10} /> {u.admin_roles.length === 1 ? u.admin_roles[0].replace(" Administrator", " admin") : `${u.admin_roles.length} admin roles`}
                    </span>
                  )}
                  {u.legacy_signins > 0 && (
                    <span className="mini-chip warn" title={(u.legacy_apps || []).join(", ")} data-testid="user-legacy">
                      <Unplug size={10} /> legacy auth ×{u.legacy_signins}
                    </span>
                  )}
                  {(u.gap_missing || []).map((g) => (
                    <span key={g} className="mini-chip warn" title={(u.gaps || []).filter((x) => x.missing.includes(g)).map((x) => x.policy).join(", ")} data-testid={`user-gap-${g}`}>
                      <ShieldAlert size={10} /> {GAP_LABEL[g] || g}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
            {!loading && data.items.length === 0 && (
              <tr>
                <td colSpan={5} className="muted center">
                  No users match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="ws-pager">
        <button type="button" className="tb-btn sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="users-prev">
          <ChevronLeft size={13} /> Prev
        </button>
        <span className="mono">
          {page} / {pages}
        </span>
        <button type="button" className="tb-btn sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="users-next">
          Next <ChevronRight size={13} />
        </button>
      </div>
    </section>
  );
}
